import * as types from '../mutation-types';
import registration from '../../api/registration';
import store from '@/store';
import _ from 'lodash';

const state = {
  referral: {
    partner: null,
    funder: null,
    sendEmail: false
  },
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  isLimited: true,
  registering: false,
  waitingForExperian: false,
  business: {
    commercialName: null,
    businessRef: null,
    registeredCompanySicCodes: null,
    BusinessLocation: {
      locationLine1: null,
      locationLine2: null,
      locationLine3: null,
      locationLine4: null,
      locationLine5: null,
      postcode: null
    }
  },
  experian: [
    {
      commercialName: null,
      businessRef: null,
      registeredCompanySicCodes: null,
      BusinessLocation: {
        locationLine1: null,
        locationLine2: null,
        locationLine3: null,
        locationLine4: null,
        locationLine5: null,
        postcode: null
      }
    }
  ],
  referrers: [],
  emailExists: false,
  businessExists: false,
  questionCount: 0,
  answers: []
};

const getters = {
  RegistrationReferrerList: state => state.referrers,
  RegistrationEmailExists: state => state.emailExists,
  RegistrationBusinessExists: state => state.businessExists,
  RegistrationExperianList: state => state.experian,
  RegistrationWaitingForExperian: state => state.waitingForExperian,
  RegistrationInProgress: state => state.registering,
  RegistrationQuestionCount: state => state.questionCount,
  RegistrationAnswers: state => state.answers
};

const actions = {
  setContact({ commit }, contact) {
    commit(types.SET_REGISTRATION_CONTACT, contact);
  },
  getRegistrationReferrerList({ commit, state }) {
    if (state.referrers.length === 0) {
      registration.getReferrerList().then(referrerList => commit(types.SET_REGISTRATION_REFERRER_LIST, referrerList));
    }
  },
  getExperianList({ commit, dispatch }, { businessName, postcode, isLimited }) {
    dispatch('setSearchingExperianFlag', true);
    return new Promise((resolve, reject) => {
      registration
        .getExperianList(businessName, postcode, isLimited)
        .then(experianList => {
          commit(types.SET_REGISTRATION_EXPERIAN_LIST, experianList);
          dispatch('setSearchingExperianFlag', false);
          resolve(experianList);
        })
        .catch(error => {
          reject(error);
          dispatch('setSearchingExperianFlag', false);
        });
    });
  },
  verifyRegistrationEmail({ commit, dispatch }, email) {
    commit(types.SET_REGISTRATION_EMAIL_EXISTS, false);
    registration.verifyEmail(email).then(email => {
      if (email.data.length > 0) {
        dispatch('addNotification', {
          text: 'Email address is already registered',
          bottom: true,
          left: true,
          colour: 'red'
        });
        commit(types.SET_REGISTRATION_EMAIL_EXISTS, true);
      } else {
        commit(types.SET_REGISTRATION_EMAIL_EXISTS, false);
      }
    });
  },
  verifyRegistrationBusiness({ commit, dispatch }, businessRef) {
    commit(types.SET_REGISTRATION_BUSINESS_EXISTS, false);
    registration.verifyBusiness(businessRef).then(business => {
      if (business.data.length > 0) {
        dispatch('addNotification', {
          text: 'Business is already registered',
          bottom: true,
          left: true,
          colour: 'red'
        });
        commit(types.SET_REGISTRATION_BUSINESS_EXISTS, true);
      } else {
        commit(types.SET_REGISTRATION_BUSINESS_EXISTS, false);
      }
    });
  },
  registerBusiness({ dispatch }, details) {
    dispatch('setRegisteringFlag', true);
    return new Promise((resolve, reject) => {
      registration
        .registerBusiness(details)
        .then(response => {
          dispatch('addNotification', {
            text: 'Customer has been successfully registered',
            colour: 'green'
          });
          resolve(response);
          dispatch('setRegisteringFlag', false);
          if (store.getters.UserHasRole('Broker')) {
            dispatch('getReferralsPage');
          } else {
            dispatch('getCustomerPage', 1);
          }
        })
        .catch(error => {
          dispatch('addNotification', {
            text: 'There was a problem registering the business',
            colour: 'red'
          });
          reject(error);
          dispatch('setRegisteringFlag', false);
        });
    });
  },
  updateRegistration({ commit, dispatch }, { updateData, referral }) {
    return new Promise((resolve, reject) => {
      registration
        .updateRegistration(updateData, referral)
        .then(response => {
          resolve(response);
          commit(types.SELECT_CUSTOMER, response.data);
        })
        .catch(error => {
          dispatch('addNotification', {
            text: 'There was a problem registering the business',
            bottom: true,
            left: true,
            colour: 'red'
          });
          console.error(error);
          reject(error);
        });
    });
  },
  clearRegistration({ commit }) {
    commit(types.CLEAR_REGISTRATION);
  },
  setRegisteringFlag({ commit }, registering) {
    commit(types.SET_REGISTERING_FLAG, registering);
  },
  setSearchingExperianFlag({ commit }, searching) {
    commit(types.SET_SEARCHING_EXPERIAN_FLAG, searching);
  },
  setRegistrationQuestionCount({ commit }, questionCount) {
    commit(types.SET_REGISTRATION_QUESTION_COUNT, questionCount);
  },
  setRegistrationAnswer({ commit }, answer) {
    commit(types.SET_REGISTRATION_ANSWER, answer);
  }
};

const mutations = {
  [types.SET_REGISTRATION_CONTACT](state, contact) {
    state.contact = contact;
  },
  [types.SET_REGISTRATION_BUSINESS](state, business) {
    state.business = business;
  },
  [types.SET_REGISTRATION_REFERRER_LIST](state, referrerList) {
    state.referrers = referrerList.data;
  },
  [types.SET_REGISTRATION_EMAIL_EXISTS](state, emailExists) {
    state.emailExists = emailExists;
  },
  [types.SET_REGISTRATION_BUSINESS_EXISTS](state, businessExists) {
    state.businessExists = businessExists;
  },
  [types.SET_REGISTRATION_EXPERIAN_LIST](state, experianList) {
    state.experian = experianList;
  },
  [types.SET_REGISTERING_FLAG](state, registering) {
    state.registering = registering;
  },
  [types.SET_SEARCHING_EXPERIAN_FLAG](state, searching) {
    state.waitingForExperian = searching;
  },
  [types.SET_REGISTRATION_QUESTION_COUNT](state, questionCount) {
    state.questionCount = questionCount;
  },
  [types.SET_REGISTRATION_ANSWER](state, answer) {
    const index = _.findIndex(state.answers, ['question_id', answer.question_id]);
    if (index >= 0) {
      _.merge(state.answers[index], answer);
    } else {
      state.answers.push(answer);
    }
  },
  // eslint-disable-next-line no-unused-vars
  [types.CLEAR_REGISTRATION](state) {
    state = {
      contact: {
        referral: {
          partner: null,
          funder: null,
          sendEmail: false
        },
        firstName: null,
        lastName: null,
        title: null,
        email: null,
        phone: null
      },
      business: {
        name: null,
        businessRef: null,
        registeredCompanySicCodes: null,
        ltdCompany: true,
        address: {
          line1: null,
          line2: null,
          line3: null,
          line4: null,
          line5: null,
          postcode: null
        }
      },
      experian: [],
      providers: [],
      partners: [],
      emailExists: false,
      businessExists: false,
      answers: []
    };
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
